import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import Decimal from 'decimal.js';
import { Plus, Trash2, Send, Save, Ban, ArrowLeft, AlertCircle } from 'lucide-react';
import { motion } from 'framer-motion';
import { PageHeader } from '@/components/PageHeader';
import { Card, CardTitle } from '@/components/Card';
import { Button } from '@/components/Button';
import { Field, Input } from '@/components/Input';
import { Combobox } from '@/components/Combobox';
import { NumericInput } from '@/components/NumericInput';
import { DocstatusPill, StatusPill } from '@/components/StatusPill';
import { Timeline } from '@/components/Timeline';
import { Kbd } from '@/components/Kbd';
import { api } from '@/lib/api';
import { money, date } from '@/lib/format';
import { toast } from '@/components/Toaster';

interface Customer  { id: string; name: string; display_name: string; default_currency?: string; }
interface Item      { id: string; code: string; name: string; stock_uom: string; standard_rate: string; }
interface Warehouse { id: string; code: string; name: string; is_group?: boolean; }
interface SOLine {
  id: string; row_index: number;
  item_id?: string; item_code: string; item_name: string;
  description?: string;
  qty: string; uom?: string; rate: string;
  amount: string; warehouse_id?: string;
}
interface SalesOrder {
  id: string; name: string;
  customer_id: string;
  transaction_date: string;
  delivery_date?: string;
  currency: string;
  exchange_rate: string;
  total: string;
  base_total: string;
  status: string;
  remarks?: string;
  docstatus: 0 | 1 | 2;
  items: SOLine[];
}

interface DraftLine {
  rowId: string;
  item_id: string;
  item_code: string;
  item_name: string;
  description: string;
  qty: string;
  uom: string;
  rate: string;
  warehouse_id: string;
}

const rid = () => Math.random().toString(36).slice(2);
const todayISO = () => new Date().toISOString().slice(0, 10);

export function SalesOrderForm() {
  const { id } = useParams({ strict: false }) as { id?: string };
  const isNew = !id || id === 'new';
  const navigate = useNavigate();
  const qc = useQueryClient();

  const { data: existing } = useQuery({
    queryKey: ['sales-order', id],
    enabled: !isNew,
    queryFn: () => api<SalesOrder>(`/accounting/sales-orders/${id}`),
  });
  const { data: customersResp  } = useQuery({ queryKey: ['customers'],  queryFn: () => api<{ items: Customer[]  }>('/accounting/customers') });
  const { data: itemsResp      } = useQuery({ queryKey: ['items'],      queryFn: () => api<{ items: Item[]      }>('/accounting/items') });
  const { data: warehousesResp } = useQuery({ queryKey: ['warehouses'], queryFn: () => api<{ items: Warehouse[] }>('/stock/warehouses') });
  const customers  = customersResp?.items ?? [];
  const items      = itemsResp?.items ?? [];
  const warehouses = useMemo(() => (warehousesResp?.items ?? []).filter((w) => !w.is_group), [warehousesResp]);

  const [customerID,  setCustomerID]  = useState('');
  const [txDate,      setTxDate]      = useState(todayISO());
  const [deliveryDate, setDeliveryDate] = useState('');
  const [currency,    setCurrency]    = useState('IDR');
  const [exchangeRate, setExchangeRate] = useState('1');
  const [remarks,     setRemarks]     = useState('');
  const [lines,       setLines]       = useState<DraftLine[]>([
    { rowId: rid(), item_id: '', item_code: '', item_name: '', description: '', qty: '1', uom: 'Unit', rate: '0', warehouse_id: '' },
  ]);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!existing) return;
    setCustomerID(existing.customer_id);
    setTxDate(existing.transaction_date.slice(0, 10));
    setDeliveryDate((existing.delivery_date ?? '').slice(0, 10));
    setCurrency(existing.currency);
    setExchangeRate(existing.exchange_rate);
    setRemarks(existing.remarks ?? '');
    setLines(existing.items.map((l) => ({
      rowId: l.id,
      item_id: l.item_id ?? '',
      item_code: l.item_code,
      item_name: l.item_name,
      description: l.description ?? '',
      qty: l.qty,
      uom: l.uom ?? 'Unit',
      rate: l.rate,
      warehouse_id: l.warehouse_id ?? '',
    })));
  }, [existing]);

  const customerOptions = useMemo(() => customers.map((c) => ({ value: c.id, label: c.display_name, description: c.name })), [customers]);
  const itemOptions     = useMemo(() => items.map((it) => ({ value: it.id, label: it.name, description: it.code })), [items]);
  const warehouseOptions = useMemo(() => warehouses.map((w) => ({ value: w.id, label: w.name, description: w.code })), [warehouses]);

  const total = useMemo(() => lines.reduce(
    (a, l) => a.plus(new Decimal(l.qty || '0').times(new Decimal(l.rate || '0'))),
    new Decimal(0),
  ), [lines]);

  const editable  = isNew;  // backend has no Update endpoint
  const submitted = existing?.docstatus === 1;
  const cancelled = existing?.docstatus === 2;

  function addLine() {
    setLines((s) => [...s, { rowId: rid(), item_id: '', item_code: '', item_name: '', description: '', qty: '1', uom: 'Unit', rate: '0', warehouse_id: '' }]);
  }
  function patch(rowId: string, p: Partial<DraftLine>) {
    setLines((s) => s.map((l) => l.rowId === rowId ? { ...l, ...p } : l));
  }
  function remove(rowId: string) {
    setLines((s) => s.length > 1 ? s.filter((l) => l.rowId !== rowId) : s);
  }
  function pickItem(rowId: string, itemID: string | null) {
    const it = items.find((i) => i.id === itemID);
    patch(rowId, {
      item_id: itemID ?? '',
      item_code: it?.code ?? '',
      item_name: it?.name ?? '',
      uom: it?.stock_uom ?? 'Unit',
      rate: it?.standard_rate ?? '0',
    });
  }

  function validate(): string[] {
    const out: string[] = [];
    if (!customerID) out.push('Customer is required.');
    if (!txDate)     out.push('Transaction date is required.');
    const valid = lines.filter((l) => l.item_id);
    if (valid.length === 0) out.push('Add at least one item line.');
    lines.forEach((l, i) => {
      if (!l.item_id) return;
      if (new Decimal(l.qty || '0').lte(0))  out.push(`Line ${i + 1}: qty must be > 0.`);
      if (new Decimal(l.rate || '0').lt(0))  out.push(`Line ${i + 1}: rate cannot be negative.`);
    });
    return out;
  }
  function buildBody() {
    return {
      customer_id:      customerID,
      transaction_date: txDate,
      delivery_date:    deliveryDate || undefined,
      currency:         currency || undefined,
      exchange_rate:    exchangeRate || undefined,
      remarks:          remarks || undefined,
      items: lines.filter((l) => l.item_id).map((l) => ({
        item_id:      l.item_id,
        item_code:    l.item_code || undefined,
        item_name:    l.item_name || undefined,
        description:  l.description || undefined,
        qty:          l.qty,
        uom:          l.uom || undefined,
        rate:         l.rate,
        warehouse_id: l.warehouse_id || undefined,
      })),
    };
  }

  const createMutation = useMutation({
    mutationFn: () => api<SalesOrder>('/accounting/sales-orders', { method: 'POST', body: buildBody() }),
    onSuccess: (so) => {
      toast.success('Draft saved', so.name);
      qc.invalidateQueries({ queryKey: ['doctype', '/accounting/sales-orders'] });
      navigate({ to: `/accounting/sales-orders/${so.id}` as never });
    },
    onError: (e: { message?: string }) => setErr(e.message ?? 'Save failed.'),
  });
  const submitMutation = useMutation({
    mutationFn: () => api<SalesOrder>(`/accounting/sales-orders/${id}/submit`, { method: 'POST' }),
    onSuccess: () => {
      toast.success('Submitted', 'Order is now active.');
      qc.invalidateQueries({ queryKey: ['sales-order', id] });
      qc.invalidateQueries({ queryKey: ['doctype', '/accounting/sales-orders'] });
    },
    onError: (e: { message?: string }) => setErr(e.message ?? 'Submit failed.'),
  });
  const cancelMutation = useMutation({
    mutationFn: () => api<SalesOrder>(`/accounting/sales-orders/${id}/cancel`, { method: 'POST' }),
    onSuccess: () => {
      toast.warning('Cancelled');
      qc.invalidateQueries({ queryKey: ['sales-order', id] });
      qc.invalidateQueries({ queryKey: ['doctype', '/accounting/sales-orders'] });
    },
    onError: (e: { message?: string }) => setErr(e.message ?? 'Cancel failed.'),
  });

  function onSaveDraft() {
    setErr(null);
    const issues = validate();
    if (issues.length > 0) { setErr(issues.join(' ')); toast.error('Fix these first', issues[0]); return; }
    createMutation.mutate();
  }

  const title = isNew ? 'New Sales Order' : (existing?.name ?? '…');

  return (
    <>
      <PageHeader
        crumbs={[
          { label: 'Finance', to: '/accounting' },
          { label: 'Sales Orders', to: '/accounting/sales-orders' },
          { label: isNew ? 'New' : title },
        ]}
        title={title}
        status={existing && <DocstatusPill docstatus={existing.docstatus} />}
        actions={
          <>
            <Button variant="ghost" asChild>
              <Link to={'/accounting/sales-orders' as never}><ArrowLeft className="size-4" /> Back</Link>
            </Button>
            {editable && (
              <Button onClick={onSaveDraft} loading={createMutation.isPending}>
                <Save className="size-4" /> Save draft <Kbd>⌘S</Kbd>
              </Button>
            )}
            {!isNew && existing?.docstatus === 0 && (
              <Button onClick={() => submitMutation.mutate()} loading={submitMutation.isPending}>
                <Send className="size-4" /> Submit
              </Button>
            )}
            {submitted && (
              <Button variant="danger" onClick={() => cancelMutation.mutate()} loading={cancelMutation.isPending}>
                <Ban className="size-4" /> Cancel
              </Button>
            )}
          </>
        }
      />

      <motion.div
        initial={{ opacity: 0, y: 4 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.15 }}
        className="flex-1 px-4 sm:px-6 lg:px-8 pt-6 pb-8 grid grid-cols-1 lg:grid-cols-[1fr,320px] gap-4 max-w-[1400px]"
      >
        <div className="space-y-4 min-w-0">
          {cancelled && (
            <Card className="!p-3 flex items-center gap-3 bg-danger/5 border-danger/30">
              <AlertCircle className="size-4 text-danger" />
              <span className="text-body text-danger">Cancelled.</span>
            </Card>
          )}
          {err && (
            <Card className="!p-3 bg-brand-error/5 border-brand-error/30 flex items-start gap-2">
              <AlertCircle className="size-4 text-brand-error mt-0.5 shrink-0" />
              <div className="text-body-sm text-brand-error">{err}</div>
            </Card>
          )}

          <Card>
            <CardTitle>Order header</CardTitle>
            <div className="mt-4 grid sm:grid-cols-2 gap-4">
              <Field label="Customer" hint="Required.">
                <Combobox
                  options={customerOptions}
                  value={customerID || null}
                  onChange={(v) => {
                    setCustomerID(v ?? '');
                    const c = customers.find((cu) => cu.id === v);
                    if (c?.default_currency) setCurrency(c.default_currency);
                  }}
                  placeholder="Pick a customer…"
                  disabled={!editable}
                />
              </Field>
              <Field label="Order date">
                <Input type="date" value={txDate} onChange={(e) => setTxDate(e.target.value)} disabled={!editable} />
              </Field>
              <Field label="Delivery date" hint="Optional — expected ship-out date.">
                <Input type="date" value={deliveryDate} onChange={(e) => setDeliveryDate(e.target.value)} disabled={!editable} />
              </Field>
              <Field label="Currency">
                <select className="input-base" value={currency} onChange={(e) => setCurrency(e.target.value)} disabled={!editable}>
                  <option value="IDR">IDR</option>
                  <option value="USD">USD</option>
                  <option value="SGD">SGD</option>
                  <option value="EUR">EUR</option>
                </select>
              </Field>
              {currency !== 'IDR' && (
                <Field label="Exchange rate" hint="Used to compute base_total.">
                  <NumericInput value={exchangeRate} onChange={setExchangeRate} decimalPlaces={4} placeholder="1.0000" />
                </Field>
              )}
              <Field label="Remarks">
                <Input value={remarks} onChange={(e) => setRemarks(e.target.value)} disabled={!editable} />
              </Field>
            </div>
          </Card>

          <Card padded={false}>
            <div className="flex items-center justify-between p-5 pb-3">
              <CardTitle>Items</CardTitle>
              {editable && (
                <Button variant="ghost" size="sm" onClick={addLine}><Plus className="size-4" /> Add line</Button>
              )}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-body-sm">
                <thead className="bg-surface-soft border-b border-hairline text-micro-uppercase text-stone">
                  <tr>
                    <th className="text-left font-medium px-3 py-2 w-10">#</th>
                    <th className="text-left font-medium px-3 py-2 min-w-[220px]">Item</th>
                    <th className="text-right font-medium px-3 py-2 w-24">Qty</th>
                    <th className="text-left font-medium px-3 py-2 w-24">UOM</th>
                    <th className="text-right font-medium px-3 py-2 w-32">Rate</th>
                    <th className="text-right font-medium px-3 py-2 w-32">Amount</th>
                    <th className="text-left font-medium px-3 py-2 min-w-[180px]">From warehouse</th>
                    <th className="text-left font-medium px-3 py-2 min-w-[180px]">Description</th>
                    {editable && <th className="w-10" />}
                  </tr>
                </thead>
                <tbody>
                  {lines.map((l, idx) => {
                    const amt = new Decimal(l.qty || '0').times(new Decimal(l.rate || '0'));
                    return (
                      <tr key={l.rowId} className="border-t border-hairline">
                        <td className="px-3 py-2 text-stone">{idx + 1}</td>
                        <td className="px-2 py-1.5">
                          {editable
                            ? <Combobox
                                options={itemOptions}
                                value={l.item_id || null}
                                onChange={(v) => pickItem(l.rowId, v)}
                                placeholder="Pick item…"
                              />
                            : <span className="px-3 text-charcoal">{l.item_name || l.item_code || l.item_id}</span>}
                        </td>
                        <td className="px-2 py-1.5">
                          {editable
                            ? <NumericInput value={l.qty} onChange={(v) => patch(l.rowId, { qty: v })} decimalPlaces={0} />
                            : <span className="num text-right block px-3">{l.qty}</span>}
                        </td>
                        <td className="px-2 py-1.5">
                          {editable
                            ? <Input value={l.uom} onChange={(e) => patch(l.rowId, { uom: e.target.value })} />
                            : <span className="px-3 text-charcoal">{l.uom}</span>}
                        </td>
                        <td className="px-2 py-1.5">
                          {editable
                            ? <NumericInput value={l.rate} onChange={(v) => patch(l.rowId, { rate: v })} decimalPlaces={0} />
                            : <span className="num text-right block px-3">{money(l.rate)}</span>}
                        </td>
                        <td className="px-3 py-2 text-right num">{money(amt.toString())}</td>
                        <td className="px-2 py-1.5">
                          {editable
                            ? <Combobox
                                options={warehouseOptions}
                                value={l.warehouse_id || null}
                                onChange={(v) => patch(l.rowId, { warehouse_id: v ?? '' })}
                                placeholder="— Any —" clearable
                              />
                            : <span className="px-3 text-charcoal">{warehouses.find((w) => w.id === l.warehouse_id)?.name || '—'}</span>}
                        </td>
                        <td className="px-2 py-1.5">
                          {editable
                            ? <Input value={l.description} onChange={(e) => patch(l.rowId, { description: e.target.value })} />
                            : <span className="px-3 text-charcoal">{l.description || '—'}</span>}
                        </td>
                        {editable && (
                          <td className="px-2 py-1.5">
                            <Button variant="ghost" size="icon" aria-label="Remove" onClick={() => remove(l.rowId)} disabled={lines.length <= 1}>
                              <Trash2 className="size-3.5" />
                            </Button>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="px-4 sm:px-5 py-3 border-t border-hairline flex flex-wrap items-center justify-end gap-x-4 gap-y-2 num">
              <div className="text-caption text-stone">Total</div>
              <div className="font-medium">{money(total.toString())}</div>
            </div>
          </Card>
        </div>

        <aside className="space-y-4">
          <Card>
            <CardTitle>Status</CardTitle>
            <div className="mt-3 space-y-2 text-body-sm">
              <Row label="Docstatus" pill={existing ? <DocstatusPill docstatus={existing.docstatus} /> : <StatusPill tone="neutral">Draft</StatusPill>} />
              {existing && (
                <>
                  <Row label="Status"   value={existing.status} />
                  <Row label="Order"    value={date(existing.transaction_date)} />
                  {existing.delivery_date && <Row label="Delivery" value={date(existing.delivery_date)} />}
                  <Row label="Total"    value={money(existing.total)} />
                  {existing.currency !== 'IDR' && <Row label="Base total" value={money(existing.base_total)} />}
                </>
              )}
            </div>
          </Card>

          <Card>
            <CardTitle>How it flows</CardTitle>
            <p className="mt-2 text-caption text-stone">
              Sales Order is the commitment from a customer before invoicing. Submit it once finalised; the SO becomes the basis for Sales Invoice or Delivery Note in the future. Cancel reverts the document. Editing a submitted SO is not supported — cancel + recreate.
            </p>
          </Card>

          {!isNew && existing && (
            <Timeline doctype="sales_order" documentId={existing.id} />
          )}
        </aside>
      </motion.div>
    </>
  );
}

function Row({ label, value, pill }: { label: string; value?: string; pill?: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className="text-stone">{label}</span>
      {pill ?? <span className="text-ink num">{value}</span>}
    </div>
  );
}
